import React, { useEffect, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { getUser } from '../services/userService';
import { hasActiveSubscription } from '../services/stripeService';
import { Card, Alert, Spinner, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getStatusBadgeVariant, isActiveSubscription } from '../utils/subscriptionUtils';

const SuccessPage = () => {
  const { user, userData, setUserData } = useUser();
  const [loading, setLoading] = useState(true);
  const [refreshed, setRefreshed] = useState(false);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const refreshUserData = async () => {
      if (user && !refreshed) {
        try {
          // Wait a moment for webhook to process
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Get fresh user data
          const freshUserData = await getUser(user.uid);
          setUserData(freshUserData);
          
          // Test subscription status
          const hasActive = await hasActiveSubscription();
          setSubscriptionActive(hasActive);
          
          setRefreshed(true);
        } catch (error) {
          console.error('Error refreshing user data:', error);
          setError('Failed to verify subscription status. Please check your account manually.');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    refreshUserData();
  }, [user, refreshed, setUserData]);

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <Card>
              <Card.Body className="text-center">
                <Spinner animation="border" className="mb-3" />
                <h5>Processing your subscription...</h5>
                <p className="text-muted">Please wait while we confirm your payment.</p>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <Card>
            <Card.Header>
              <h4 className="mb-0">
                {subscriptionActive ? '🎉 Payment Successful!' : '✅ Payment Processed'}
              </h4>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="warning" className="mb-4">
                  <Alert.Heading>Verification Issue</Alert.Heading>
                  <p>{error}</p>
                </Alert>
              )}
              
              {subscriptionActive ? (
                <Alert variant="success" className="mb-4">
                  <Alert.Heading>Welcome to PulseSync Premium!</Alert.Heading>
                  <p>
                    Your subscription has been successfully activated. You now have access to all premium features.
                  </p>
                </Alert>
              ) : (
                <Alert variant="info" className="mb-4">
                  <Alert.Heading>Thank you for your purchase!</Alert.Heading>
                  <p>
                    Your payment has been processed successfully. Your subscription may take a few minutes to activate. 
                    If you don't see your subscription status updated, please refresh the page or check back later.
                  </p>
                </Alert>
              )}

              {userData && (
                <div className="mb-4">
                  <h5>Subscription Details</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Status:</strong> 
                        <Badge bg={getStatusBadgeVariant(userData.subscriptionStatus)} className="ms-2">
                          {userData.subscriptionStatus}
                        </Badge>
                      </p>
                      <p><strong>Email:</strong> {userData.email}</p>
                    </div>
                    <div className="col-md-6">
                      {userData.currentPeriodEnd && (
                        <p><strong>Next Billing:</strong> {new Date(userData.currentPeriodEnd).toLocaleDateString()}</p>
                      )}
                      {userData.stripeCustomerId && (
                        <p><strong>Customer ID:</strong> <small className="text-muted">{userData.stripeCustomerId}</small></p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/')}
                >
                  Go to Dashboard
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
